import { useId } from "react";
import { useTranslation } from "react-i18next";

import { Box } from "@mui/material";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useStyles } from "./WorkspaceConfigurationPanel.style";
import { getWorkspaceSettingLabelKey, WorkspacePage } from "./WorkspaceConfigurationPanel.utils";
import { WorkspaceSettingRow } from "./WorkspaceSettingRow";

type WorkspacePageSettingCardProps = {
    page: WorkspacePage;
    valueByName: Record<API.WorkspaceSettingName, 0 | 1>;
    isRowBusy: (name: API.WorkspaceSettingName) => boolean;
    onToggle: (name: API.WorkspaceSettingName, nextChecked: boolean) => void;
};

export function WorkspacePageSettingCard(props: WorkspacePageSettingCardProps) {
    const { page, valueByName, isRowBusy, onToggle } = props;
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["pages", "sidepanels"]);

    const titleId = useId();

    const isPageEnabled = valueByName[page.name] === 1;
    const pageTitle = t(page.navigationLabelKey);

    return (
        <SectionPanel
            data-testid={`workspacePageSettingCard-${page.name}`}
            title={
                <Box className={classes.cardTitle} id={titleId}>
                    <i className={cx(page.icon, classes.pageIcon)} aria-hidden />
                    {pageTitle}
                </Box>
            }
        >
            <WorkspaceSettingRow
                name={page.name}
                label={t("pages:workspacePage.configuration.showPageInNavigation")}
                value={valueByName[page.name]}
                isBusy={isRowBusy(page.name)}
                labelledByCardId={titleId}
                onToggle={onToggle}
            />

            {page.features.map((feature) => (
                <WorkspaceSettingRow
                    key={feature.name}
                    name={feature.name}
                    label={t(getWorkspaceSettingLabelKey(feature.name))}
                    value={valueByName[feature.name]}
                    isBusy={isRowBusy(feature.name)}
                    labelledByCardId={titleId}
                    disabledReason={
                        isPageEnabled ? undefined : t("pages:workspacePage.configuration.featureNeedsPage", { page: pageTitle })
                    }
                    onToggle={onToggle}
                />
            ))}
        </SectionPanel>
    );
}
